/**
 * **Stok, bahan & resep** (0069 · §9).
 *
 * ### ⛔⛔ Yang paling penting di layar ini adalah angka CAKUPAN
 * VTEC menerbitkan 135.593 dokumen konsumsi bahan, 92% kosong — dan dari layar, modul itu terlihat
 * bekerja sempurna. Yang membedakan kita bukan tabelnya melainkan pengakuan: **berapa persen nilai
 * penjualan yang benar-benar tertutup resep**, dicetak besar, di atas segalanya.
 *
 * ⛔ Diukur NILAI, bukan jumlah menu. *"142 dari 640 menu"* terdengar seperti seperempat jalan;
 * kalau 142 itu menu terlaris, ia bisa berarti 78% nilai penjualan — kesimpulan yang berbeda total,
 * dan hanya satu dari keduanya yang bisa dipakai memutuskan resep mana ditulis berikutnya.
 *
 * ### ⚠️ "Teoretis" ditulis apa adanya
 * Saldo di layar ini sebagian **hitungan**, bukan pengamatan. Yang akan menghitung fisik berhak
 * tahu berapa banyak — kalau tidak, ia menganggap angka layar sebagai kebenaran dan menyalahkan
 * hitungannya sendiri.
 */
import {
  Alert, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Statistic,
  Table, Tabs, Tag, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Coverage, Material, MovementRow, OnHandRow, Outlet, Product, RecipeRow } from '../api/client';

/** ⚠️ Alasan susut = daftar TERTUTUP. Teks bebas berakhir 18% kosong (bukti VTEC). */
const ALASAN_SUSUT = ['spoilage', 'breakage', 'expired', 'staff_meal', 'other'] as const;

export function Stock() {
  const { t } = useTranslation();
  const [outlets, setOutlets] = useState<readonly Outlet[]>([]);
  const [outletCode, setOutletCode] = useState<string | undefined>(undefined);
  const [onHand, setOnHand] = useState<readonly OnHandRow[]>([]);
  const [movements, setMovements] = useState<readonly MovementRow[]>([]);
  const [materials, setMaterials] = useState<readonly Material[]>([]);
  const [recipes, setRecipes] = useState<readonly RecipeRow[]>([]);
  const [products, setProducts] = useState<readonly Product[]>([]);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(29, 'day'), dayjs()]);
  const [busy, setBusy] = useState(false);
  const [gerakan, setGerakan] = useState<'receive' | 'waste' | null>(null);
  const [resepUntuk, setResepUntuk] = useState<string | null>(null);
  const [bahanBaru, setBahanBaru] = useState(false);
  const [formGerak] = Form.useForm();
  const [formBahan] = Form.useForm();
  const [formResep] = Form.useForm();

  const muat = useCallback(async () => {
    setBusy(true);
    const from = range[0].format('YYYY-MM-DD');
    const to = range[1].format('YYYY-MM-DD');
    try {
      const [m, r, c] = await Promise.all([
        api.materials(),
        api.recipes(),
        api.recipeCoverage(from, to, outletCode),
      ]);
      setMaterials(m);
      setRecipes(r.rows);
      setCoverage(c);
      if (outletCode !== undefined) {
        const [oh, mv] = await Promise.all([
          api.stockOnHand(outletCode),
          api.stockMovements(outletCode, from, to),
        ]);
        setOnHand(oh.rows);
        setMovements(mv.rows);
      } else {
        setOnHand([]);
        setMovements([]);
      }
    } catch (e) {
      void message.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [outletCode, range]);

  useEffect(() => {
    void api.outlets().then(setOutlets).catch(() => undefined);
    void api.products().then(setProducts).catch(() => undefined);
  }, []);
  useEffect(() => { void muat(); }, [muat]);

  const pemilihOutlet = (
    <Select
      allowClear
      style={{ minWidth: 220 }}
      placeholder={t('stock.pickOutlet')}
      value={outletCode}
      onChange={setOutletCode}
      data-testid="stock-outlet"
      options={outlets.map((o) => ({ value: o.code, label: `${o.code} — ${o.name}` }))}
    />
  );

  const kolomSaldo: ColumnsType<OnHandRow> = [
    {
      title: t('stock.colKind'), dataIndex: 'kind', width: 100,
      render: (v: string) => <Tag>{t(`stock.kind.${v}`)}</Tag>,
    },
    { title: t('stock.colCode'), dataIndex: 'code', width: 140 },
    { title: t('stock.colName'), dataIndex: 'name' },
    { title: t('stock.colUom'), dataIndex: 'uom', width: 80, render: (v: string | null) => v ?? '—' },
    {
      title: t('stock.colOnHand'), dataIndex: 'qtyOnHand', align: 'right', width: 130,
      render: (v: string) => <b>{trimQty(v)}</b>,
    },
    {
      title: t('stock.colTheoretical'), dataIndex: 'theoreticalSince', align: 'right', width: 170,
      render: (v: string) => (
        /* ⚠️ Dinyatakan, bukan disembunyikan — lihat catatan modul. */
        Number(v) === 0 ? <span style={{ opacity: 0.5 }}>—</span> : (
          <Typography.Text type="warning" data-testid="stock-theoretical">{trimQty(v)}</Typography.Text>
        )
      ),
    },
  ];

  const kolomGerak: ColumnsType<MovementRow> = [
    { title: t('stock.colDate'), dataIndex: 'businessDate', width: 110 },
    { title: t('stock.colCode'), dataIndex: 'code', width: 140 },
    {
      title: t('stock.colType'), dataIndex: 'movementType', width: 190,
      render: (v: string, r) => (
        <Space size={4}>
          <Tag>{t(`stock.type.${v}`)}</Tag>
          {r.isTheoretical ? (
            <Tag color="orange" data-testid="stock-theoretical-tag">{t('stock.theoretical')}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('stock.colQty'), dataIndex: 'qty', align: 'right', width: 120,
      render: (v: string) => (
        <span style={{ color: Number(v) < 0 ? '#a8071a' : undefined }}>{trimQty(v)}</span>
      ),
    },
    { title: t('stock.colReason'), dataIndex: 'reasonCode', render: (v: string | null) => v ?? '—' },
    { title: t('stock.colOrder'), dataIndex: 'orderNo', width: 130, render: (v: string | null) => v ?? '—' },
    { title: t('stock.colActor'), dataIndex: 'actorName', width: 130, render: (v: string | null) => v ?? '—' },
  ];

  const kolomBahan: ColumnsType<Material> = [
    { title: t('stock.colCode'), dataIndex: 'code', width: 160 },
    { title: t('stock.colName'), dataIndex: 'name' },
    { title: t('stock.colUom'), dataIndex: 'uom', width: 100 },
  ];

  const kolomResep: ColumnsType<RecipeRow> = [
    { title: t('stock.colProduct'), dataIndex: 'product_code', width: 160 },
    { title: t('stock.colName'), dataIndex: 'product_name' },
    {
      title: t('stock.colYield'), dataIndex: 'yield_qty', width: 100, align: 'right',
      render: (v: string) => trimQty(v),
    },
    {
      title: t('stock.colLines'),
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {r.lines.map((l) => (
            <Tag key={l.materialCode}>{`${l.materialName} ${trimQty(l.qty)} ${l.uom}`}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '', width: 90,
      render: (_: unknown, r) => (
        <Button size="small" onClick={() => { bukaResep(r.product_code); }}>
          {t('common.edit')}
        </Button>
      ),
    },
  ];

  function bukaResep(productCode: string) {
    const ada = recipes.find((r) => r.product_code === productCode);
    setResepUntuk(productCode);
    formResep.setFieldsValue({
      productCode,
      yieldQty: ada?.yield_qty ?? '1',
      lines: ada?.lines.map((l) => ({ materialCode: l.materialCode, qty: l.qty })) ?? [{}],
    });
  }

  async function simpanResep() {
    const v = await formResep.validateFields();
    try {
      const lines = ((v['lines'] ?? []) as { materialCode?: string; qty?: string }[])
        .filter((l) => (l.materialCode ?? '') !== '')
        .map((l) => ({ materialCode: String(l.materialCode), qty: String(l.qty ?? '0') }));
      const r = await api.saveRecipe({
        productCode: String(v['productCode']),
        yieldQty: String(v['yieldQty'] ?? '1'),
        lines,
      });
      void (r.deleted ? message.warning(t('stock.recipeDeleted')) : message.success(t('common.saved')));
      setResepUntuk(null);
      await muat();
    } catch (e) {
      void message.error((e as Error).message);
    }
  }

  async function simpanBahan() {
    const v = await formBahan.validateFields();
    try {
      await api.saveMaterial({
        code: String(v['code']), name: String(v['name']), uom: String(v['uom']),
      });
      setBahanBaru(false);
      formBahan.resetFields();
      await muat();
    } catch (e) {
      void message.error((e as Error).message);
    }
  }

  async function simpanGerakan() {
    const v = await formGerak.validateFields();
    if (outletCode === undefined) return;
    const target = pecahTarget(String(v['target']));
    const body = {
      outletCode,
      target,
      qty: String(v['qty']),
      businessDate: dayjs(v['businessDate'] as Dayjs).format('YYYY-MM-DD'),
      note: (v['note'] as string | undefined) ?? null,
    };
    try {
      if (gerakan === 'receive') await api.stockReceive(body);
      else await api.stockWaste({ ...body, reasonCode: String(v['reasonCode']) });
      setGerakan(null);
      formGerak.resetFields();
      await muat();
    } catch (e) {
      void message.error((e as Error).message);
    }
  }

  const opsiSasaran = [
    ...materials.map((m) => ({ value: `material:${m.code}`, label: `${m.name} (${m.uom})` })),
    ...products.map((p) => ({ value: `product:${p.code}`, label: `${p.name} — ${p.code}` })),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/*
        ⭐⭐⭐ Cakupan di ATAS segalanya, dan sengaja besar. Modul stok yang tak mengaku berapa
        banyak yang tak dihitungnya akan dipercaya sepenuhnya — dan itu persis yang terjadi di VTEC
        selama bertahun-tahun.
      */}
      <Card size="small">
        <Space size="large" align="start" wrap>
          <Statistic
            title={t('stock.coverageTitle')}
            value={coverage?.coveredPercent ?? '0.0'}
            suffix="%"
            data-testid="stock-coverage"
          />
          <div style={{ maxWidth: 520 }}>
            <Typography.Text type="secondary">{t('stock.coverageHelp')}</Typography.Text>
            {coverage !== null && coverage.topUncovered.length > 0 ? (
              <div style={{ marginTop: 6 }} data-testid="stock-uncovered">
                <Typography.Text strong>{t('stock.topUncovered')}</Typography.Text>{' '}
                {coverage.topUncovered.slice(0, 5).map((u) => (
                  <Tag key={u.productCode}>{`${u.productName} · ${u.share}%`}</Tag>
                ))}
              </div>
            ) : null}
          </div>
          <DatePicker.RangePicker
            value={range}
            onChange={(v) => { if (v?.[0] && v[1]) setRange([v[0], v[1]]); }}
          />
          {pemilihOutlet}
        </Space>
      </Card>

      <Tabs
        items={[
          {
            key: 'onhand',
            label: t('stock.tabOnHand'),
            children: (
              <Card
                size="small"
                extra={
                  <Space>
                    <Button
                      data-testid="stock-receive"
                      disabled={outletCode === undefined}
                      onClick={() => {
                        setGerakan('receive');
                        formGerak.setFieldsValue({ businessDate: dayjs() });
                      }}
                    >
                      {t('stock.receive')}
                    </Button>
                    <Button
                      danger
                      data-testid="stock-waste"
                      disabled={outletCode === undefined}
                      onClick={() => {
                        setGerakan('waste');
                        formGerak.setFieldsValue({ businessDate: dayjs() });
                      }}
                    >
                      {t('stock.waste')}
                    </Button>
                  </Space>
                }
              >
                {outletCode === undefined ? (
                  <Alert type="info" showIcon message={t('stock.pickOutletFirst')} />
                ) : (
                  <Table
                    rowKey={(r) => `${r.kind}:${r.code}`}
                    size="small"
                    loading={busy}
                    columns={kolomSaldo}
                    dataSource={[...onHand]}
                    pagination={false}
                    data-testid="stock-onhand"
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'movements',
            label: t('stock.tabMovements'),
            children: (
              <Card size="small">
                <Table
                  rowKey="id"
                  size="small"
                  loading={busy}
                  columns={kolomGerak}
                  dataSource={[...movements]}
                  pagination={{ pageSize: 25 }}
                  data-testid="stock-movements"
                />
              </Card>
            ),
          },
          {
            key: 'recipes',
            label: t('stock.tabRecipes'),
            children: (
              <Card
                size="small"
                extra={
                  <Select
                    showSearch
                    style={{ minWidth: 260 }}
                    placeholder={t('stock.addRecipe')}
                    value={null}
                    onChange={(v: string) => { bukaResep(v); }}
                    data-testid="stock-add-recipe"
                    options={products.map((p) => ({ value: p.code, label: `${p.name} — ${p.code}` }))}
                  />
                }
              >
                <Table
                  rowKey="product_code"
                  size="small"
                  loading={busy}
                  columns={kolomResep}
                  dataSource={[...recipes]}
                  pagination={false}
                  data-testid="stock-recipes"
                />
              </Card>
            ),
          },
          {
            key: 'materials',
            label: t('stock.tabMaterials'),
            children: (
              <Card
                size="small"
                extra={
                  <Button type="primary" data-testid="stock-add-material" onClick={() => { setBahanBaru(true); }}>
                    {t('stock.addMaterial')}
                  </Button>
                }
              >
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 10 }}
                  message={t('stock.uomWarning')}
                />
                <Table
                  rowKey="id"
                  size="small"
                  loading={busy}
                  columns={kolomBahan}
                  dataSource={[...materials]}
                  pagination={false}
                  data-testid="stock-materials"
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        open={gerakan !== null}
        title={gerakan === 'waste' ? t('stock.waste') : t('stock.receive')}
        onCancel={() => { setGerakan(null); }}
        onOk={() => void simpanGerakan()}
        okButtonProps={{ 'data-testid': 'stock-save-movement' } as never}
      >
        <Form form={formGerak} layout="vertical">
          <Form.Item name="target" label={t('stock.colName')} rules={[{ required: true }]}>
            <Select showSearch options={opsiSasaran} data-testid="stock-target" />
          </Form.Item>
          <Form.Item name="qty" label={t('stock.colQty')} rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} step={0.001} stringMode />
          </Form.Item>
          <Form.Item name="businessDate" label={t('stock.colDate')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          {gerakan === 'waste' ? (
            <Form.Item
              name="reasonCode"
              label={t('stock.colReason')}
              /* ⛔ Wajib — server menolak susut tanpa alasan, dan layar mengatakannya lebih dulu. */
              rules={[{ required: true, message: t('stock.reasonRequired') }]}
            >
              <Select
                data-testid="stock-reason"
                options={ALASAN_SUSUT.map((r) => ({ value: r, label: t(`stock.reason.${r}`) }))}
              />
            </Form.Item>
          ) : null}
          <Form.Item name="note" label={t('stock.colNote')}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={bahanBaru}
        title={t('stock.addMaterial')}
        onCancel={() => { setBahanBaru(false); }}
        onOk={() => void simpanBahan()}
        okButtonProps={{ 'data-testid': 'stock-save-material' } as never}
      >
        <Form form={formBahan} layout="vertical">
          <Form.Item name="code" label={t('stock.colCode')} rules={[{ required: true }]}>
            <Input data-testid="material-code" />
          </Form.Item>
          <Form.Item name="name" label={t('stock.colName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="uom"
            label={t('stock.colUom')}
            extra={t('stock.uomWarning')}
            rules={[{ required: true }]}
          >
            <Input placeholder="g / ml / pcs" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={resepUntuk !== null}
        title={t('stock.recipeTitle', { code: resepUntuk ?? '' })}
        width={640}
        onCancel={() => { setResepUntuk(null); }}
        onOk={() => void simpanResep()}
        okButtonProps={{ 'data-testid': 'stock-save-recipe' } as never}
      >
        <Form form={formResep} layout="vertical">
          <Form.Item name="productCode" hidden><Input /></Form.Item>
          <Form.Item
            name="yieldQty"
            label={t('stock.colYield')}
            extra={t('stock.yieldHelp')}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: 200 }} min={0.000001} step={1} stringMode />
          </Form.Item>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map((f) => (
                  <Space key={f.key} align="baseline" style={{ display: 'flex', marginBottom: 4 }}>
                    <Form.Item name={[f.name, 'materialCode']} style={{ marginBottom: 0, minWidth: 300 }}>
                      <Select
                        showSearch
                        placeholder={t('stock.tabMaterials')}
                        options={materials.map((m) => ({
                          value: m.code, label: `${m.name} (${m.uom})`,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item name={[f.name, 'qty']} style={{ marginBottom: 0 }}>
                      <InputNumber min={0.000001} step={0.001} stringMode placeholder="0" />
                    </Form.Item>
                    <Button size="small" danger onClick={() => { remove(f.name); }}>×</Button>
                  </Space>
                ))}
                <Button size="small" onClick={() => { add(); }} data-testid="recipe-add-line">
                  + {t('stock.addLine')}
                </Button>
              </>
            )}
          </Form.List>
          {/* ⭐ Dikatakan terang-terangan: resep kosong DIHAPUS, tak disimpan kosong (Guardrail #8). */}
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 10 }}
            message={t('stock.emptyRecipeWarning')}
          />
        </Form>
      </Modal>
    </Space>
  );
}

/** Angka kuantitas untuk MATA: `150.000000` → `150`. ⛔ Tak pernah dipakai berhitung. */
function trimQty(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(Number(n.toFixed(6))) : v;
}

function pecahTarget(v: string): { kind: string; code: string } {
  const i = v.indexOf(':');
  return { kind: v.slice(0, i), code: v.slice(i + 1) };
}
